/**
 * Broker Deduplication modals + hook.
 *
 * Three modals (one per dedup kind) plus a `useDedupGuard` hook that
 * wraps create operations with a live duplicate-check round-trip. The
 * pattern is:
 *
 *   const guard = useDedupGuard({ kind: 'broker', token });
 *   const onSubmit = async (formData) => {
 *     const decision = await guard.check(formData);
 *     // decision is one of:
 *     //   { proceed: true }                      — no duplicates
 *     //   { proceed: true, override_id: '...' }  — user overrode
 *     //   { proceed: false }                     — user cancelled
 *     //   { proceed: false, transfer_to: id }    — agent transfer
 *     //   { proceed: false, conflict_raised: true } — manager pinged
 *     if (decision.proceed) {
 *       await actuallyCreate({ ...formData, dedup_override_id: decision.override_id });
 *     }
 *   };
 *
 * The hook owns the modal state; consumer renders `<guard.Modal />`.
 * That separation keeps page components from juggling modal flags.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  AlertTriangle, AlertOctagon, ArrowRightLeft, CheckCircle2,
  Building2, User, Phone, Mail, IdCard, Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Confidence colours — keep in sync with the spec's flag legend so
// the warning modals and the inline badges read consistently.
const SCORE_TONE = (score) => {
  if (score >= 95) return 'bg-red-100 text-red-700 border-red-300';      // near-certain dupe
  if (score >= 85) return 'bg-amber-100 text-amber-700 border-amber-300'; // possible dupe
  return 'bg-slate-100 text-slate-700 border-slate-300';
};

/**
 * Company-duplicate modal. Shown after the live check returns ≥1
 * candidate when creating / editing a broker. Three exits:
 *   - "Use Existing"      → consumer should re-target the editing
 *                            flow to the candidate, NOT create a new
 *                            broker. We expose the chosen id via
 *                            `onUseExisting(candidate)`.
 *   - "Continue Anyway"   → records an override + proceeds.
 *   - "Cancel"            → discards.
 */
const CompanyDedupModal = ({
  open, onOpenChange, candidates, seed, onUseExisting, onOverride, onCancel,
}) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={20} />
            Possible duplicate broker found
          </DialogTitle>
          <DialogDescription>
            You're about to create <strong>"{seed?.name || seed?.company || '—'}"</strong>,
            which looks like an existing broker. Pick an action below.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {candidates.map((c) => (
            <div
              key={c.id}
              className="border border-slate-200 dark:border-slate-700 rounded-md p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Building2 size={14} className="text-slate-500 flex-shrink-0" />
                    <span className="font-semibold">{c.name || c.company}</span>
                    <Badge className={`text-[10px] border ${SCORE_TONE(c._match_score)}`}>
                      {c._match_score}% match
                    </Badge>
                    {c._is_auto_merge_candidate && (
                      <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">
                        RERA confirmed
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{c._match_reason}</p>
                  <div className="flex items-center gap-3 mt-1.5 text-xs text-slate-600 dark:text-slate-300 flex-wrap">
                    {c.email && <span className="flex items-center gap-1"><Mail size={11} />{c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
                    {c.rera_number && <span className="flex items-center gap-1"><IdCard size={11} />RERA {c.rera_number}</span>}
                    {(c.total_sales !== undefined) && (
                      <span className="text-slate-400">· {c.total_sales} sale{c.total_sales === 1 ? '' : 's'}</span>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 flex-shrink-0"
                  onClick={() => onUseExisting?.(c)}
                >
                  <CheckCircle2 size={13} className="mr-1" /> Use existing
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-600">
            If you continue anyway, briefly explain why (audit trail) —
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. genuinely different company with same name, different trade license…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onCancel?.(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-500 text-white"
            disabled={!reason.trim()}
            onClick={() => {
              onOverride?.(reason.trim());
              onOpenChange(false);
            }}
          >
            Continue Anyway — Mark as New
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Agent-duplicate modal. The most useful exit is the "Transfer" path
 * — when a known agent shows up under a new company, we treat it as
 * a company change rather than creating a parallel record.
 */
const AgentDedupModal = ({
  open, onOpenChange, candidates, seed, onTransfer, onOverride, onCancel,
}) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="text-amber-500" size={20} />
            Agent already exists
          </DialogTitle>
          <DialogDescription>
            <strong>{seed?.name || 'This agent'}</strong> looks like an
            existing record at another broker company. Is this a company
            change, or genuinely a different person?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {candidates.map((c) => (
            <div
              key={c.id}
              className="border border-slate-200 dark:border-slate-700 rounded-md p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <User size={14} className="text-slate-500 flex-shrink-0" />
                    <span className="font-semibold">{c.name}</span>
                    <Badge className={`text-[10px] border ${SCORE_TONE(c._match_score)}`}>
                      {c._match_score}% match
                    </Badge>
                    {(c._match_reasons || []).includes('rera_card') && (
                      <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">
                        RERA card match
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Currently at <strong>{c.broker_name || '—'}</strong>.
                    Matched by: {(c._match_reasons || []).join(', ')}.
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-slate-600 dark:text-slate-300 flex-wrap">
                    {c.email && <span className="flex items-center gap-1"><Mail size={11} />{c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Phone size={11} />{c.phone}</span>}
                    {c.rera_card && <span className="flex items-center gap-1"><IdCard size={11} />RERA {c.rera_card}</span>}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 border-violet-300 text-violet-700 hover:bg-violet-50 flex-shrink-0"
                  onClick={() => onTransfer?.(c)}
                >
                  <ArrowRightLeft size={13} className="mr-1" /> Transfer
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="space-y-1">
          <label className="text-xs text-slate-600">
            If you add as new instead, briefly explain why —
          </label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="e.g. different person with same name, agent has multiple RERA cards…"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { onCancel?.(); onOpenChange(false); }}>
            Cancel
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-500 text-white"
            disabled={!reason.trim()}
            onClick={() => {
              onOverride?.(reason.trim());
              onOpenChange(false);
            }}
          >
            Add as New
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Lead/client conflict modal — most consequential of the three, since
 * a wrong call here means a broker doesn't get commission. We do NOT
 * offer a quiet "override" path — the only ways forward are flag for
 * manager review or cancel. The frontend can pass `allowOverride={true}`
 * for admin users where appropriate, but it's off by default.
 */
const LeadConflictModal = ({
  open, onOpenChange, candidates, seed, onFlagConflict, onCancel, onOverride,
  allowOverride = false,
}) => {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);
  if (!open) return null;

  // Show the most-recent / highest-confidence candidate prominently;
  // the rest go in a "see more" list. Usually there's just one.
  const primary = candidates[0];
  const rest = candidates.slice(1);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onCancel?.(); onOpenChange(v); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertOctagon className="text-red-600" size={22} />
            Client already registered — conflict
          </DialogTitle>
          <DialogDescription>
            This client appears on another broker's books. A duplicate
            registration risks a commission dispute — flag it to your
            sales manager so they can decide ownership.
          </DialogDescription>
        </DialogHeader>

        {primary && (
          <div className="border border-red-200 bg-red-50 dark:bg-red-950/20 rounded-md p-3 space-y-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{primary.buyer_name || seed?.buyer_name || '—'}</span>
              <Badge variant="outline" className="text-[10px] border-red-300 text-red-700">
                Match: {(primary._match_reasons || []).join(', ')}
              </Badge>
            </div>
            <p className="text-xs text-slate-600 dark:text-slate-300">
              Already registered by <strong>{primary.broker_name}</strong>
              {primary.agent_name && <span> (agent {primary.agent_name})</span>}
              {primary.created_at && (
                <span> on {new Date(primary.created_at).toLocaleDateString()}</span>
              )}.
            </p>
            {primary.property_name && (
              <p className="text-xs text-slate-500">
                Property: {primary.property_name}
                {primary.unit_number && ` · Unit ${primary.unit_number}`}
              </p>
            )}
            <p className="text-xs text-slate-500">
              Status: {primary.payment_status || 'pending'}
            </p>
          </div>
        )}

        {rest.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-500">
              + {rest.length} more registration{rest.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-1 space-y-1">
              {rest.map((c) => (
                <li key={c.id}>
                  <strong>{c.broker_name}</strong> on {c.created_at && new Date(c.created_at).toLocaleDateString()}
                </li>
              ))}
            </ul>
          </details>
        )}

        {allowOverride && (
          <div className="space-y-1">
            <label className="text-xs text-slate-600">
              If you override (admin only), explain why —
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. prior relationship documented in lead history…"
            />
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={() => { onCancel?.(); onOpenChange(false); }}>
            Cancel
          </Button>
          {allowOverride && (
            <Button
              variant="outline"
              className="border-amber-300 text-amber-700 hover:bg-amber-50"
              disabled={!reason.trim()}
              onClick={() => { onOverride?.(reason.trim()); onOpenChange(false); }}
            >
              Override with reason
            </Button>
          )}
          <Button
            className="bg-red-600 hover:bg-red-500 text-white"
            onClick={() => { onFlagConflict?.(primary); onOpenChange(false); }}
          >
            <AlertOctagon size={13} className="mr-1.5" />
            Flag for sales manager
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook: orchestrates the live-check → modal → decision dance.
 *
 * Usage (see header of this file for the full pattern):
 *   const guard = useDedupGuard({ kind: 'broker', token });
 *   ...
 *   <guard.Modal />
 *   ...
 *   const decision = await guard.check(formPayload);
 *
 * The hook never throws. If the check API errors out, we log a warning
 * toast and decide `proceed: true` with no override — degrading
 * gracefully is better than blocking a legitimate broker create on a
 * transient network blip.
 */
export const useDedupGuard = ({ kind, token, isAdmin = false }) => {
  const [open, setOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [seed, setSeed] = useState(null);
  const [resolver, setResolver] = useState(null);

  const resolveWith = useCallback((decision) => {
    if (resolver) resolver(decision);
    setOpen(false);
    setCandidates([]);
    setSeed(null);
    setResolver(null);
  }, [resolver]);

  const recordOverride = useCallback(async (reason, cands) => {
    try {
      const res = await axios.post(
        `${API}/broker-dedup/overrides?token=${token}`,
        {
          kind,
          seed_payload: seed,
          candidates: cands.map((c) => ({ id: c.id })),
          reason,
        },
      );
      return res.data?.data?.override_id || null;
    } catch (e) {
      console.warn('[Dedup] could not record override:', e);
      // Don't block the user — proceed without the override id. The
      // manager won't see it in their digest but the broker still
      // gets created.
      return null;
    }
  }, [kind, seed, token]);

  /** Run the live check; returns a decision object. */
  const check = useCallback(async (payload) => {
    setSeed(payload);
    const endpoint = {
      broker: 'check/company',
      agent: 'check/agent',
      lead: 'check/lead',
    }[kind];
    if (!endpoint) {
      return { proceed: true };
    }

    let cands = [];
    try {
      const res = await axios.post(
        `${API}/broker-dedup/${endpoint}?token=${token}`,
        payload,
      );
      cands = res.data?.data?.candidates || [];
    } catch (e) {
      console.warn('[Dedup] live check failed, proceeding without:', e);
      return { proceed: true };
    }

    if (cands.length === 0) {
      return { proceed: true };
    }

    // Show the modal and return a Promise that resolves when the user
    // picks an action. Putting the resolver in state means the modal
    // body's onClick handlers can fire it.
    setCandidates(cands);
    return new Promise((resolve) => {
      setResolver(() => resolve);
      setOpen(true);
    });
  }, [kind, token]);

  /** Render-prop helper — caller drops `<guard.Modal />` once. */
  const Modal = useMemo(() => {
    const ModalComponent = () => {
      if (kind === 'broker') {
        return (
          <CompanyDedupModal
            open={open}
            onOpenChange={(v) => !v && resolveWith({ proceed: false })}
            candidates={candidates}
            seed={seed}
            onUseExisting={(c) => resolveWith({ proceed: false, use_existing: c })}
            onOverride={async (reason) => {
              const id = await recordOverride(reason, candidates);
              resolveWith({ proceed: true, override_id: id, reason });
            }}
            onCancel={() => resolveWith({ proceed: false })}
          />
        );
      }
      if (kind === 'agent') {
        return (
          <AgentDedupModal
            open={open}
            onOpenChange={(v) => !v && resolveWith({ proceed: false })}
            candidates={candidates}
            seed={seed}
            onTransfer={(c) => resolveWith({ proceed: false, transfer_from: c })}
            onOverride={async (reason) => {
              const id = await recordOverride(reason, candidates);
              resolveWith({ proceed: true, override_id: id, reason });
            }}
            onCancel={() => resolveWith({ proceed: false })}
          />
        );
      }
      if (kind === 'lead') {
        return (
          <LeadConflictModal
            open={open}
            onOpenChange={(v) => !v && resolveWith({ proceed: false })}
            candidates={candidates}
            seed={seed}
            allowOverride={isAdmin}
            onFlagConflict={async (primary) => {
              // Park the new sale by creating a conflict row instead of
              // posting to /broker-sales. The manager resolves it in
              // the Conflict Resolution screen.
              try {
                await axios.post(
                  `${API}/broker-dedup/conflicts?token=${token}`,
                  {
                    existing_sale_id: primary?.id,
                    new_sale_payload: seed,
                    reason_for_override: 'flagged_by_user',
                  },
                );
                toast.success('Flagged for sales manager review');
              } catch (e) {
                toast.error(e?.response?.data?.detail || 'Could not flag conflict');
              }
              resolveWith({ proceed: false, conflict_raised: true });
            }}
            onOverride={async (reason) => {
              const id = await recordOverride(reason, candidates);
              resolveWith({ proceed: true, override_id: id, reason });
            }}
            onCancel={() => resolveWith({ proceed: false })}
          />
        );
      }
      return null;
    };
    return ModalComponent;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, candidates, seed, kind, isAdmin]);

  return { check, Modal };
};

export default useDedupGuard;
